import * as React from 'react';
import Keypad, { KeypadRef } from './Keypad';
import { Content, Footer, LargeDeviceOnly, PhoneOnly, SaleHeader, TicketControl, VQuickActionsPanel } from 'pos/ui';
import { TicketLineDTO, ProductForSaleDTO, Res, FlowAction, ProductGroupForSaleDTO, ProductGroupType, SearchMode, WhenPrint, SoldProductType } from 'core/api';
import * as SalesStore from '../SalesStore';
import * as Images from 'pos/Images';
import * as Selection from './ProductSelectionState';
import { Command } from 'pos/common';
import { SearchByText } from './SearchByText';
import { ActionButtonDefinitionFactory, HQuickActionsPanel, PosScreen, ThemeManager } from 'pos/ui';
import { SaleScreenProps } from './SaleScreenProps';
import { Box, HStack, Separator, Stack, VStack } from '@chakra-ui/react';
import { SessionProvider } from 'core/infrastructure';
import { CatalogueFacade } from 'pos/facades';
import { createSaleScreenActions } from './createSaleScreenActions';
import { useBarcodeReaderRef } from 'core/infrastructure-next';
import { LargeDeviceLastSale, PhoneLastSale } from './LastSale';
import { TicketTotal } from './TicketTotal';
import { LargeDeviceHeaderContent, PhoneHeaderContent } from './HeaderContent';
import { useProductComposer } from './useProductComposer';
import { useFunctionKeyOn } from './useFunctionKeyOn';
import { getLastSelectableLine, useSaleScreenEvents } from './useSaleScreenEvents';
import { LargeDeviceSearchByProductGroup, PhoneSearchByProductGroup } from './SearchByProductGroup';
import * as CoreImages from 'core/img';
import { ModalHooks, useOverlayExecutor } from 'core/ui-next/modal';

const getPhoneScreenMode = (selection: Selection.ProductsSelection): 'groups' | 'products' | 'subgroup' => {
	if (selection.selectedGroup && selection.selectedSubgroup)
		return 'subgroup';

	if (selection.selectedGroup)
		return 'products';

	return 'groups';
};

// Guardo el estado en que se quedó el ticket (expandido o no)
let expandTicketOnCreation = false;
export default function SaleScreen(props: SaleScreenProps) {

	const { close, cancel, businessDay } = props;

	const keypadRef = React.useRef<KeypadRef>(null);

	const [selection, setSelection] = React.useState(Selection.createInitialForPos(props.groups, expandTicketOnCreation, props.initialSelection));

	const [searchMode, setSearchMode] = React.useState(SessionProvider.getCurrent().SearchMode);
	const [selectedLine, setSelectedLine] = React.useState<Nullable<TicketLineDTO>>(getLastSelectableLine(SalesStore.getTicket(), null));
	const [expandedTicket, setExpandTicket] = React.useState(false);
	const [hasCashControlNotification, setHasCashControlNotification] = React.useState(false);
	const [hasTakeOutOrdersNotification, setHasTakeOutOrdersNotification] = React.useState(false);
	const [printMode, setPrintMode] = React.useState(SessionProvider.getTicketWhenPrint());
	const { execute } = useOverlayExecutor();


	const applyFlowAction = React.useCallback(async (action: FlowAction) => {
		if (action === FlowAction.DoNothing)
			return;
		close(action);
	}, [close]);

	const clearIntroducedText = React.useCallback(() => {
		keypadRef.current?.clear();
	}, []);

	const getIntroducedText = React.useCallback(() => {
		return keypadRef.current?.getRawValue() || '';
	}, []);

	const getIntroducedQuantity = React.useCallback(() => {
		return keypadRef.current?.getValue();
	}, []);

	const onPrintModeChanged = React.useCallback((mode: WhenPrint) => {
		setPrintMode(mode);
	}, [setPrintMode]);

	const onToggleSearchMode = React.useCallback(() => {
		setSearchMode(searchMode === SearchMode.Text ? SearchMode.ProductGroup : SearchMode.Text);
		setSelection(Selection.clearGroupSelection(Selection.clearProducts(selection)));
	}, [setSearchMode, searchMode, selection]);


	const { composeProduct } = useProductComposer({ selection, setSelection });

	const clearKeypadIfNeeded = React.useCallback((productSelected: ProductForSaleDTO) => {
		// Si es una subfamilia o una subcategoría no limpiamos el KeyPad, ya que queremos
		// mantener el valor introducido para cuando seleccione el producto
		if (productSelected.IsSubFamily || productSelected.IsSubCategory)
			return;

		keypadRef.current?.clear();
	}, []);

	const selectLine = React.useCallback((line: Nullable<TicketLineDTO>) => {
		setSelectedLine(line);
	}, [setSelectedLine]);

	const onProductSelected = React.useCallback(async (productSelected: ProductForSaleDTO, quantity?: number) => {

		const result = await composeProduct(productSelected, quantity ?? getIntroducedQuantity());
		clearKeypadIfNeeded(productSelected);

		return result;
	}, [composeProduct, clearKeypadIfNeeded, getIntroducedQuantity]);

	const onProductGroupSelected = React.useCallback(async (group: ProductGroupForSaleDTO) => {
		// Esta función no hace nada si el grupo es una producto de venta directa.
		// Es necesario invocarla siempre para que acabe haciendo el hideOtherOperations si es necesario
		setSelection(await Selection.selectGroup(selection, group, SalesStore.getTicket().Id));

		if (group.Type === ProductGroupType.ProductForDirectSale) {
			const product = await CatalogueFacade.getSaleableAsMainByProductId(group.Id, SalesStore.getTicket().Id);
			await onProductSelected(product, getIntroducedQuantity());
			clearIntroducedText();
			return;
		}
	}, [clearIntroducedText, onProductSelected, getIntroducedQuantity, selection]);

	const reloadPrices = React.useCallback(async () => {
		setSelection(await Selection.updateOnPricesChanged(selection, SalesStore.getTicket().Id));
	}, [setSelection, selection]);

	const onEditLine = React.useCallback(async (line: TicketLineDTO) => {
		await execute({
			action: async () => {
				await Command.execute('editTicketLine', line, {
					showAddNotesOrEditMenu: true,
					showChangeAddins: true,
					showAdjustIngredients: true,
					showRemoveLine: true,
					showSetPreparationOrder: true,
					showSetTicketLineCreator: true,
					showPrepare: true,
					showProductDetails: true,
					showRequest: true,
					showSetDiscounts: true,
					showSetPrice: true,
					showSetQuantity: true,
					showEditSeatNumber: line.Type !== SoldProductType.MenuItem
				});
			}
		});
	}, [execute]);

	const onRemoveLine = React.useCallback(async (line: TicketLineDTO) => {
		await Command.execute('removeTicketLine', line);
	}, []);

	useSaleScreenEvents({
		productsSelection: selection,
		selectLine,
		setProductSelection: setSelection,
		setHasCashControlNotification,
		setHasTakeOutOrdersNotification,
		selectedLine,
		applyFlowAction
	});

	React.useEffect(() => {
		expandTicketOnCreation = expandedTicket;
	}, [expandedTicket]);

	const buttonActions = createSaleScreenActions({
		hasCashControlNotification: () => hasCashControlNotification,
		hasTakeOutOrdersNotification: () => hasTakeOutOrdersNotification,
		applyFlowAction,
		clearIntroducedText,
		getIntroducedText,
		getIntroducedQuantity,
		getPrintMode: () => printMode,
		getSearchMode: () => searchMode,
		getSelectedLine: () => selectedLine,
		onPrintModeChanged,
		onToggleSearchMode,
		onProductSelected,
		onReloadPrices: reloadPrices
	});

	const onToggleKeyBoard = React.useCallback(() => {
		setExpandTicket(!expandedTicket);
	}, [setExpandTicket, expandedTicket]);

	const onBarcodeRead = React.useCallback(async (barcode: string) => {
		await Command.execute('sellByBarcode', barcode, onProductSelected);
	}, [onProductSelected]);

	useBarcodeReaderRef(onBarcodeRead);

	const onSearchProducts = React.useCallback(async (text: string, showOnlyThisPosProducts?: boolean, showOnlySaleableProducts?: boolean) =>
		setSelection(await Selection.searchProductsByText(selection, text, SalesStore.getTicket().Id, selection.sortMode, showOnlySaleableProducts, showOnlyThisPosProducts)), [selection]);

	const onSortProductsBy = React.useCallback(async (sortMode: Selection.SortMode) =>
		setSelection(Selection.sortProducts(selection, sortMode)), [selection]);

	const onClearSearch = React.useCallback(async () => setSelection(Selection.clearProducts(selection)), [selection]);

	const onBack = React.useCallback(async () => {

		if (ThemeManager.isLargeDevice()) {
			cancel();
			return;
		}

		const screenMode = getPhoneScreenMode(selection);
		if (screenMode === 'products') {
			setSelection(Selection.clearGroupSelection(selection));
			return;
		}

		if (screenMode === 'subgroup') {
			setSelection(await Selection.selectGroup(selection, selection.selectedGroup!, SalesStore.getTicket().Id));
			return;
		}

		cancel();
	}, [selection, cancel]);

	ModalHooks.useOnDismiss(async () => {
		await onBack();
	});

	const quickActionRows = props.profile.MainViewQuickButtonRows;
	const quickActionColumns = props.profile.MainViewQuickButtonColumns;
	const otherActionRows = props.profile.MainViewOtherButtonRows - 1;
	const otherActionColumns = props.profile.MainViewOtherButtonColumns;
	const { quickButtons, otherButtons } = ActionButtonDefinitionFactory.createForSaleScreen(props.customActionButtons, props.profile, buttonActions);

	useFunctionKeyOn(quickButtons);

	const showLastSale = SalesStore.getTicket().Lines.length === 0 && SalesStore.getPreparationOrder() === null && SalesStore.getLastSale() !== null;

	const numberOfLinesToShow = props.profile.NumberOfTicketLines ?? 1;

	const onUserClick = () => execute({
		action: async () => {
			close(FlowAction.ChangeUser);
		},
		waitForPendingRequests: true
	});

	return (
		<PosScreen screenId='SALE' size={'full-screen'}>

			<SaleHeader
				content={
					ThemeManager.isPhone()
						? <PhoneHeaderContent ticket={SalesStore.getTicket()} seatNumber={SalesStore.getSeatNumber()} preparationOrder={SalesStore.getPreparationOrder()} />
						: <LargeDeviceHeaderContent ticket={SalesStore.getTicket()} businessDay={businessDay} seatNumber={SalesStore.getSeatNumber()} preparationOrder={SalesStore.getPreparationOrder()} onBack={onBack} onCloseDay={buttonActions.closeDay} />
				}
				onUserClick={onUserClick}
			/>

			<Content>
				<PhoneOnly>
					<VStack bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }} width={'full'} height={'full'} overflow={'auto'} gap={'0'}>
						<VStack width={'full'} bg='inherit' position={showLastSale ? undefined : 'sticky'} top={0} zIndex={1}>
							{
								showLastSale
									? <VStack padding={'2'} width={'full'} gap={'4'}>
										<PhoneLastSale lastSale={SalesStore.getLastSale()!} />
										<Separator width={'full'} borderColor={'transparent'} bg={{ base: '{colors.neutral.300}', _dark: '{colors.neutral.300}' }} height={'1.25px'} />
									</VStack>
									: <Stack w={'full'} gap={'2'} padding={'2'}>
										<Box w="full" h={`${(numberOfLinesToShow * 44) / 16}rem`} overflowY={'auto'}>
											<TicketControl
												selectedLine={selectedLine}
												ticket={SalesStore.getTicket()}
												onSelect={selectLine}
												onEdit={async (line) => await onEditLine(line)}
												onDelete={async (line) => await onRemoveLine(line)}
												hasEditionInLine={true}
												expandedTicket={expandedTicket}
												numberOfLinesToShow={numberOfLinesToShow}
											/>
										</Box>
										<TicketTotal ticket={SalesStore.getTicket()} />
									</Stack>
							}
						</VStack>
						<VStack padding={'2'} width={'full'} bg={'inherit'}>
							<VStack width={'full'}>
								<PhoneSearchByProductGroup
									mode={getPhoneScreenMode(selection)}
									selection={selection}
									onGroupSelected={onProductGroupSelected}
									buttonsSize={props.profile.ButtonSize}
									onProductSelected={onProductSelected}
								/>
							</VStack>
						</VStack>
					</VStack>

				</PhoneOnly>

				<LargeDeviceOnly>
					<HStack w='full' h='full' p='2' bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }}>
						<Stack h='full' w='96' flexShrink={0}>
							{
								showLastSale
									? <Stack flex={1} justifyContent={'center'} alignItems={'center'} bg={{ base: 'white', _dark: '{colors.neutral.900}' }} borderRadius={'sm'}>
										<LargeDeviceLastSale lastSale={SalesStore.getLastSale()!} />
									</Stack>
									: <Box flex={1} overflowY='auto' w='full'>
										<TicketControl
											selectedLine={selectedLine}
											ticket={SalesStore.getTicket()}
											onSelect={selectLine}
											onEdit={async (line) => await onEditLine(line)}
											onDelete={async (line) => await onRemoveLine(line)}
											hasEditionInLine={true}
											expandedTicket={expandedTicket}
										/>
									</Box>
							}
							<TicketTotal ticket={SalesStore.getTicket()} onToggleKeyboard={onToggleKeyBoard} keypadIcon={Images.Keypad} isExpandedTicket={expandedTicket} />
							{
								expandedTicket ? null : <Box flexShrink={0} width={'full'} bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }}><Keypad ref={keypadRef} line={selectedLine} /></Box>
							}
						</Stack>
						<VQuickActionsPanel surface='sales-screen' columns={quickActionColumns} rows={quickActionRows} buttons={quickButtons} extraButtons={{ rows: otherActionRows, columns: otherActionColumns, buttons: otherButtons }} />
						{
							searchMode === SearchMode.ProductGroup
								? <Stack w='full' h='full' overflow={'hidden'}>
									<LargeDeviceSearchByProductGroup
										productGroupRows={props.profile.NumberOfProductGroupRows}
										selection={selection}
										onGroupSelected={onProductGroupSelected}
										buttonsSize={props.profile.ButtonSize}
										onProductSelected={onProductSelected}
									/>
								</Stack>
								: <Box flex={1} h='full' className='legacy-styles'>
									<SearchByText
										products={selection.products}
										onProductSelected={onProductSelected}
										onSearchProducts={onSearchProducts}
										onClearSearch={onClearSearch}
										onSortProductsBy={onSortProductsBy}
										sortBy={selection.sortMode}
									/>
								</Box>
						}
					</HStack>
				</LargeDeviceOnly>
			</Content>

			<Footer>
				<PhoneOnly>
					<HQuickActionsPanel buttons={[{ title: Res.Common.BACK_BTN(), image: CoreImages.Back, action: onBack }, ...quickButtons]} surface='footer' extraButtons={{
						rows: 5, // En las comanderas las filas y las columnas son fijas. No son configurables
						columns: 5,
						buttons: otherButtons
					}} />
				</PhoneOnly>
			</Footer>

		</PosScreen >
	);
}
